import { Dispatch, useCallback } from 'react'
import type {
  CustomFieldRenderProps,
  RenderCustomFieldProps,
  RenderCustomFieldSelectConfig,
} from '@factorialco/f0-react'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import type { SelectionMetadata } from 'modules/core/components/EntitySelectorV2/types'

import type { EmployeeSelectorFieldConfig, FactorialCustomFieldConfig } from './types'

// Identity function to convert narrowed `object` to Record without `as` operator
function toRecord(obj: object): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj))
}

function isManualMultiValue(val: unknown): val is { type: 'manual'; ids: string[] } {
  if (val === null || typeof val !== 'object') return false
  const obj = toRecord(val)
  return 'type' in obj && obj['type'] === 'manual' && 'ids' in obj && Array.isArray(obj['ids'])
}

function renderEmployeeSelector(
  props: CustomFieldRenderProps<unknown, EmployeeSelectorFieldConfig>
) {
  const { value, onChange, disabled, placeholder, config, label } = props
  const { forcedFilters, options, hint, messages, useAccessId, filters, mandatory, multiple } =
    config

  if (multiple) {
    const multiValue = isManualMultiValue(value) ? value.ids : []

    const handleMultiChange: (
      state: EmployeeSelectorV2MultiSelectState,
      _metadata: SelectionMetadata
    ) => void = (state, _metadata) => {
      onChange(state)
    }

    return (
      <EmployeeSelectorV2
        multiple
        label={label}
        value={multiValue}
        onChange={handleMultiChange}
        options={options}
        forcedFilters={forcedFilters}
        disabled={disabled}
        hint={hint}
        messages={{ placeholder, ...messages }}
        useAccessId={useAccessId}
        filters={filters}
        mandatory={mandatory}
      />
    )
  }

  const stringValue = typeof value === 'string' ? value : undefined
  const handleChange: Dispatch<string | undefined> = (v) => {
    onChange(v)
  }

  return (
    <EmployeeSelectorV2
      label={label}
      value={stringValue}
      onChange={handleChange}
      options={options}
      forcedFilters={forcedFilters}
      disabled={disabled}
      hint={hint}
      messages={{ placeholder, ...messages }}
      useAccessId={useAccessId}
      filters={filters}
      mandatory={mandatory}
    />
  )
}

function isFactorialCustomFieldConfig(config: unknown): config is FactorialCustomFieldConfig {
  if (config === null || typeof config !== 'object') return false
  const obj = toRecord(config)
  return 'type' in obj && typeof obj['type'] === 'string'
}

export function useEmployeeFormFieldRenderer() {
  return useCallback(
    (props: RenderCustomFieldProps): React.ReactNode | RenderCustomFieldSelectConfig | null => {
      if (!props.config || !isFactorialCustomFieldConfig(props.config)) return null

      const { type } = props.config

      switch (type) {
        case 'employee-selector': {
          const employeeProps: CustomFieldRenderProps<unknown, EmployeeSelectorFieldConfig> = {
            ...props,
            config: props.config,
          }
          return renderEmployeeSelector(employeeProps)
        }
        default:
          return null
      }
    },
    []
  )
}
